/**
 * 週次pt集計チャートの要件定義ビュー（要件定義抜き / 内訳）用の系列定義。
 * WeeklyPointTrendChart の max-code-lines (500) を避けるため分離している。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次pt集計チャートの要件定義ビュー用に、チャート行の値（要件定義を除く実績・目標 / 要件定義pt・予算）と、内訳ビューの要件定義pt 棒・目標線（予算 / 要件定義予算 / 全部ビューの個人積み上げ）の系列、凡例 / ツールチップのラベルを組み立てる
 * @graph-connects none
 */

import type { ReactElement } from 'react';
import { Bar, LabelList, Line } from 'recharts';
import { BAR_MAX_SIZE, BAR_RADIUS_TOP } from './chart-styles.js';
import { REQUIREMENT_COLOR, type DevelopPointView } from './develop-point-view.js';
import type { KpiWeeklyTrendPoint } from '../../../types/api.js';

/** 内訳ビューの「目標pt（要件定義除く）」線の色（開発pt 棒の黄より濃い色で棒と区別する） @graph-connects none */
const DEVELOP_TARGET_COLOR = '#a16207';
/** 内訳ビューの「目標pt（要件定義）」線の色（要件定義 棒の水色より濃い色で棒と区別する） @graph-connects none */
const REQUIREMENT_TARGET_COLOR = '#0284c7';
/** 全部 / 要件定義抜きビューの目標pt 線の色（従来色） @graph-connects none */
const DEFAULT_TARGET_COLOR = '#7dd3fc';
/** 目標pt（個人積み上げ）線の色（従来色） @graph-connects none */
const COMBAT_POWER_TARGET_COLOR = '#fb923c';

/** 週クリック（棒・目標線の点）のハンドラ @graph-connects none */
type WeekClickHandler = (data: { weekStart?: string; weekEnd?: string }) => void;

/**
 * 要件定義ビューのチャート行の値。全部ビューの行（要件定義込み）を上書きする。
 *  - チーム別積み上げ（byTeam_*）は呼び出し側で要件定義を除いた内訳に差し替える
 *  - 最新週の処理中pt の上乗せは全部ビューと同じく行う（処理中pt は要件定義を分けられないので総量のまま）
 *  - 個人積み上げは「個人積み上げ − 要件定義予算」、月内累計は要件定義を除いた値に差し替える
 * @graph-connects none
 */
export function requirementViewChartFields(row: KpiWeeklyTrendPoint, wipTotal: number) {
  const develop = row.developActualExcludingRequirement ?? 0;
  const requirement = row.requirementDefinitionActual ?? 0;
  return {
    actual: develop,
    actualWithWip: develop + wipTotal,
    wip: wipTotal,
    requirementActual: requirement,
    actualTotal: develop + wipTotal + requirement,
    target: row.developTargetExcludingRequirement,
    requirementTarget: row.requirementDefinitionTarget,
    targetCombatPower: row.developTargetCombatPowerExcludingRequirement,
    cumulativeActual: row.cumulativeDevelopActualWithWipExcludingRequirement,
    cumulativeTarget: row.cumulativeDevelopTargetExcludingRequirement,
    cumulativeTargetCombatPower: row.cumulativeDevelopTargetCombatPowerExcludingRequirement,
    achievementRate: row.achievementRateExcludingRequirement,
    achievementRateCombatPower: row.achievementRateCombatPowerExcludingRequirement,
  };
}

/**
 * 系列名 → 凡例 / ツールチップの i18n キー（実績・目標・個人積み上げ・要件定義の系列のみ。それ以外は null）
 * @graph-connects none
 */
export function weeklySeriesLabelKey(name: string, view: DevelopPointView): string | null {
  const excludeRequirement = view !== 'all';
  if (name === 'actual' || name === 'actualWithWip') {
    return excludeRequirement ? 'kpi.weeklyActualExcludingRequirement' : 'kpi.weeklyActual';
  }
  if (name === 'target') {
    return excludeRequirement ? 'kpi.weeklyTargetExcludingRequirement' : 'kpi.weeklyTarget';
  }
  if (name === 'targetCombatPower') {
    return excludeRequirement
      ? 'kpi.weeklyTargetCombatPowerExcludingRequirement'
      : 'kpi.weeklyTargetCombatPower';
  }
  if (name === 'requirementActual') return 'kpi.weeklyTable.requirementDefinition';
  if (name === 'requirementTarget') return 'kpi.weeklyRequirementTarget';
  return null;
}

/** 棒の上に出す合計ラベル（0 は出さない） @graph-connects none */
const formatBarTotal = ((value: number) =>
  value > 0 ? Math.round(value).toLocaleString() : '') as never;

/**
 * 内訳ビューで実績棒（チーム別積み上げ、または単色）の上に積む要件定義pt の棒。
 * 積み上げの最上段になるので、棒の上に合計（要件定義込みの実績pt）を出す。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次pt集計チャートの内訳ビューで、要件定義を除く実績棒の上に要件定義pt を積み上げ、合計ラベルを付ける
 * @graph-connects Bar [calls] 要件定義pt 棒の描画
 */
export function renderRequirementSegment(onBarClick: WeekClickHandler | undefined): ReactElement {
  const clickProps = onBarClick
    ? { onClick: onBarClick as never, style: { cursor: 'pointer' } }
    : {};
  return (
    <Bar
      key="requirementActual"
      dataKey="requirementActual"
      name="requirementActual"
      stackId="actual"
      fill={REQUIREMENT_COLOR}
      fillOpacity={0.85}
      stroke={REQUIREMENT_COLOR}
      strokeWidth={1}
      maxBarSize={BAR_MAX_SIZE}
      radius={BAR_RADIUS_TOP}
      {...clickProps}
    >
      <LabelList
        dataKey="actualTotal"
        position="top"
        fill="var(--color-text-primary)"
        fontSize={11}
        formatter={formatBarTotal}
      />
    </Bar>
  );
}

/** 目標線の点の描画設定（週クリックが有効なら点クリックでも週詳細を開く） @graph-connects none */
function targetDots(color: string, onDotClick: WeekClickHandler | undefined) {
  return {
    dot: { r: 3, fill: color },
    activeDot: onDotClick
      ? { r: 5, fill: color, style: { cursor: 'pointer' }, onClick: onDotClick as never }
      : { r: 5, fill: color },
  };
}

/**
 * 週次pt集計チャートの目標線。
 *  - 目標pt: 全部 / 要件定義抜きは従来の水色。内訳は要件定義 棒が水色なので開発pt 系の色にする
 *  - 目標pt（要件定義）: 内訳ビューだけ
 *  - 目標pt（個人積み上げ）: 全ビュー。要件定義ビューでは API が要件定義予算を差し引いた値を返す
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次pt集計チャートの目標線（予算 / 内訳ビューの要件定義予算 / 個人積み上げ。要件定義ビューの個人積み上げは要件定義予算を差し引いた値）を口径に応じて組み立てる
 * @graph-connects Line [calls] 目標線の描画
 */
export function renderWeeklyTargetLines(
  view: DevelopPointView,
  onDotClick: WeekClickHandler | undefined,
): ReactElement[] {
  const targetColor = view === 'breakdown' ? DEVELOP_TARGET_COLOR : DEFAULT_TARGET_COLOR;
  const lines = [
    <Line
      key="target"
      type="monotone"
      dataKey="target"
      name="target"
      stroke={targetColor}
      strokeWidth={1.5}
      strokeDasharray="5 3"
      {...targetDots(targetColor, onDotClick)}
    />,
  ];
  if (view === 'breakdown') {
    lines.push(
      <Line
        key="requirementTarget"
        type="monotone"
        dataKey="requirementTarget"
        name="requirementTarget"
        stroke={REQUIREMENT_TARGET_COLOR}
        strokeWidth={1.5}
        strokeDasharray="5 3"
        {...targetDots(REQUIREMENT_TARGET_COLOR, onDotClick)}
        connectNulls={false}
      />,
    );
  }
  lines.push(
    <Line
      key="targetCombatPower"
      type="monotone"
      dataKey="targetCombatPower"
      name="targetCombatPower"
      stroke={COMBAT_POWER_TARGET_COLOR}
      strokeWidth={1.5}
      {...targetDots(COMBAT_POWER_TARGET_COLOR, onDotClick)}
      connectNulls={false}
    />,
  );
  return lines;
}
